import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Box,
  Check,
  ChevronDown,
  Copy,
  FileBadge,
  FileCheck,
  FolderTree,
  Gem,
  Globe,
  Loader2,
  MapPin,
  Monitor,
  Network,
  ScrollText,
  Server,
  Shield,
  ShieldCheck,
  User,
  Users,
  X,
} from "lucide-react";
import { useADStore, type ADGraphNode } from "../../store/adStore";
import {
  fetchAssetGroupTags,
  fetchEntityInfo,
  fetchEntityRelationship,
  fetchEntityRelationshipGraph,
  fetchNodeGraph,
  type BloodHoundAssetGroupTag,
  type BloodHoundEntityInfo,
  type BloodHoundRelatedEntity,
} from "../../services/adApi";
import {
  GRAPH_RENDER_LIMIT,
  LABEL_TAG_TYPE,
  SOURCE_KINDS,
  TAG_KIND_PREFIX,
  ZONE_TAG_TYPE,
  getEntityType,
  getSectionsForEntityType,
  type InspectorSection,
} from "./inspector/inspectorConfig";
import { buildObjectFields, fieldValueText, formatPrimitive, type InspectorField } from "./inspector/formatters";
import "./ADInspector.css";

const PAGE_SIZE = 128;

const KIND_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  User: User,
  ADLocalUser: User,
  Group: Users,
  ADLocalGroup: Users,
  Computer: Monitor,
  Domain: Globe,
  OU: FolderTree,
  Container: Box,
  GPO: ScrollText,
  CertTemplate: FileBadge,
  IssuancePolicy: FileCheck,
  EnterpriseCA: ShieldCheck,
  RootCA: ShieldCheck,
  AIACA: ShieldCheck,
  NTAuthStore: ShieldCheck,
  Site: MapPin,
  SiteServer: Server,
  SiteSubnet: Network,
};

const KIND_COLORS: Record<string, string> = {
  User: "#0ea5e9",
  ADLocalUser: "#0ea5e9",
  Group: "#10b981",
  ADLocalGroup: "#10b981",
  Computer: "#f59e0b",
  Domain: "#ef4444",
  OU: "#06b6d4",
  Container: "#8b5cf6",
  GPO: "#ec4899",
};

const DEFAULT_KIND_COLOR = "#64748b";

const tagCache = new Map<string, Promise<BloodHoundAssetGroupTag[]>>();

const loadTags = (tenantName: string) => {
  if (!tagCache.has(tenantName)) {
    tagCache.set(
      tenantName,
      fetchAssetGroupTags(tenantName).catch(() => {
        tagCache.delete(tenantName);
        return [];
      }),
    );
  }
  return tagCache.get(tenantName)!;
};

const tagNameToKind = (name: string) => `${TAG_KIND_PREFIX}${name.split(" ").join("_")}`;

const getPrimaryKind = (kinds: string[], fallback: string) =>
  kinds.find((kind) => kind && !kind.startsWith(TAG_KIND_PREFIX) && !SOURCE_KINDS.has(kind)) ?? fallback;

const collectRelationships = (sections: InspectorSection[]): string[] =>
  sections.flatMap((section) => (section.relationship ? [section.relationship] : collectRelationships(section.sections ?? [])));

const requestErrorMessage = (error: unknown) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 500) return "The request could not be completed, possibly due to the volume of objects involved.";
  if (status === 504) return "The results took too long to complete, possibly due to the volume of objects involved.";
  return "An unknown error occurred during the request.";
};

const isAbort = (error: unknown) => axios.isCancel(error) || (error as { name?: string })?.name === "CanceledError";

interface RelationshipCount {
  count?: number;
  loading: boolean;
  error?: unknown;
}

function KindIcon({ kind, className }: { kind: string; className?: string }) {
  const Icon = KIND_ICONS[kind] ?? Shield;
  return <Icon className={className} />;
}

function KindTile({ kind, size = "md" }: { kind: string; size?: "sm" | "md" }) {
  const color = KIND_COLORS[kind] ?? DEFAULT_KIND_COLOR;
  return (
    <span className="adi-kind-tile" data-size={size} style={{ color, backgroundColor: `${color}14`, borderColor: `${color}33` }}>
      <KindIcon kind={kind} />
    </span>
  );
}

function CopyValueButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="adi-copy"
      title="Copy value"
      aria-label="Copy value"
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check /> : <Copy />}
    </button>
  );
}

function FieldRow({ field }: { field: InspectorField }) {
  const text = fieldValueText(field.value);
  const isList = Array.isArray(field.value);
  const isLong = !isList && text.length > 42;
  return (
    <div className="adi-field" data-stacked={isList || isLong}>
      <span className="adi-field-label">{field.label}</span>
      {isList ? (
        <ul className="adi-field-list">
          {(field.value as string[]).map((item, index) => (
            <li key={`${field.key}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <span className="adi-field-value">{text}</span>
      )}
      <CopyValueButton value={text} />
    </div>
  );
}

function CollapsibleHeader({
  label,
  count,
  loading,
  error,
  expanded,
  disabled,
  depth,
  onToggle,
}: {
  label: string;
  count?: number;
  loading?: boolean;
  error?: boolean;
  expanded: boolean;
  disabled?: boolean;
  depth: number;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="adi-section-header"
      aria-expanded={expanded}
      disabled={disabled}
      data-depth={depth}
      onClick={onToggle}
    >
      <ChevronDown className="adi-section-chevron" />
      <span className="adi-section-label">{label}</span>
      {loading ? (
        <Loader2 className="adi-section-spinner" />
      ) : error ? (
        <AlertTriangle className="adi-section-error-icon" />
      ) : count !== undefined ? (
        <span className="adi-count" data-empty={count === 0}>
          {count.toLocaleString()}
        </span>
      ) : null}
    </button>
  );
}

function RelationshipList({
  tenantName,
  entityType,
  objectId,
  relationship,
  total,
  onOpen,
}: {
  tenantName: string;
  entityType: string;
  objectId: string;
  relationship: string;
  total: number;
  onOpen: (item: BloodHoundRelatedEntity) => void;
}) {
  const [items, setItems] = useState<BloodHoundRelatedEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const sentinelRef = useRef<HTMLLIElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    async (skip: number) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      controllerRef.current = new AbortController();
      try {
        const page = await fetchEntityRelationship(tenantName, entityType, objectId, relationship, {
          skip,
          limit: PAGE_SIZE,
          signal: controllerRef.current.signal,
        });
        setItems((current) => (skip === 0 ? page.data : [...current, ...page.data]));
      } catch (requestError) {
        if (!isAbort(requestError)) setError(requestError);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [tenantName, entityType, objectId, relationship],
  );

  useEffect(() => {
    void loadPage(0);
    return () => controllerRef.current?.abort();
  }, [loadPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || items.length >= total) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadPage(items.length);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [items.length, total, loadPage]);

  if (error && items.length === 0) {
    return <p className="adi-inline-error">{requestErrorMessage(error)}</p>;
  }

  return (
    <ul className="adi-related-list">
      {items.map((item, index) => {
        const id = item.objectID || item.props?.objectid || "";
        const name = item.name || item.objectID || item.props?.name || item.props?.objectid || "Unknown";
        const kind = item.label || item.kind || "";
        return (
          <li key={`${id}-${index}`}>
            <button type="button" className="adi-related-item" title={name} disabled={!id} onClick={() => onOpen(item)}>
              <KindTile kind={kind} size="sm" />
              <span className="adi-related-name">{name}</span>
            </button>
          </li>
        );
      })}
      {(loading || items.length < total) && (
        <li ref={sentinelRef} className="adi-related-loading">
          <Loader2 className="adi-section-spinner" />
          Loading
        </li>
      )}
    </ul>
  );
}

function SectionTree({
  sections,
  depth,
  parentPath,
  expandedPath,
  counts,
  onToggle,
  renderList,
}: {
  sections: InspectorSection[];
  depth: number;
  parentPath: string[];
  expandedPath: string[];
  counts: Record<string, RelationshipCount>;
  onToggle: (path: string[], section: InspectorSection, expanded: boolean) => void;
  renderList: (section: InspectorSection, total: number) => JSX.Element;
}) {
  return (
    <>
      {sections.map((section) => {
        const path = [...parentPath, section.label];
        const expanded = expandedPath.length >= path.length && path.every((label, index) => expandedPath[index] === label);
        const leaves = section.relationship ? [section.relationship] : collectRelationships(section.sections ?? []);
        const states = leaves.map((relationship) => counts[relationship] ?? { loading: true });
        const loading = states.some((state) => state.loading);
        const failed = states.every((state) => state.error);
        const count = states.reduce((total, state) => total + (state.count ?? 0), 0);
        return (
          <div key={section.label} className="adi-section" data-depth={depth}>
            <CollapsibleHeader
              label={section.label}
              count={loading || failed ? undefined : count}
              loading={loading}
              error={!loading && failed}
              expanded={expanded}
              disabled={loading || (count === 0 && !failed)}
              depth={depth}
              onToggle={() => onToggle(path, section, !expanded)}
            />
            {expanded && (
              <div className="adi-section-body">
                {section.relationship ? (
                  failed ? (
                    <p className="adi-inline-error">{requestErrorMessage(states[0]?.error)}</p>
                  ) : (
                    renderList(section, count)
                  )
                ) : (
                  <SectionTree
                    sections={section.sections ?? []}
                    depth={depth + 1}
                    parentPath={path}
                    expandedPath={expandedPath}
                    counts={counts}
                    onToggle={onToggle}
                    renderList={renderList}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function InspectorPanel({ node, tenantName }: { node: ADGraphNode; tenantName: string }) {
  const { selectNode, loadGraph } = useADStore();
  const [entityInfo, setEntityInfo] = useState<BloodHoundEntityInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [tags, setTags] = useState<BloodHoundAssetGroupTag[]>([]);
  const [objectInfoOpen, setObjectInfoOpen] = useState(true);
  const [expandedPath, setExpandedPath] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, RelationshipCount>>({});
  const [navigationError, setNavigationError] = useState<string>();

  const kinds = entityInfo?.kinds?.length ? entityInfo.kinds : node.kinds;
  const primaryKind = getPrimaryKind(kinds ?? [], node.kind);
  const entityType = getEntityType(primaryKind) ?? getEntityType(node.kind);
  const objectId = node.objectId || (node.properties?.objectid as string) || "";
  const properties = useMemo(
    () => ({ ...node.properties, ...(entityInfo?.props ?? {}) }) as Record<string, unknown>,
    [node.properties, entityInfo],
  );
  const sections = useMemo(() => getSectionsForEntityType(entityType), [entityType]);
  const relationships = useMemo(() => collectRelationships(sections), [sections]);

  useEffect(() => {
    let active = true;
    loadTags(tenantName).then((result) => active && setTags(result));
    return () => {
      active = false;
    };
  }, [tenantName]);

  useEffect(() => {
    if (!entityType || !objectId) return;
    const controller = new AbortController();
    setInfoLoading(true);
    fetchEntityInfo(tenantName, entityType, objectId, controller.signal)
      .then(setEntityInfo)
      .catch(() => setEntityInfo(null))
      .finally(() => !controller.signal.aborted && setInfoLoading(false));
    return () => controller.abort();
  }, [tenantName, entityType, objectId]);

  useEffect(() => {
    if (!entityType || !objectId || relationships.length === 0) {
      setCounts({});
      return;
    }
    const controller = new AbortController();
    setCounts(Object.fromEntries(relationships.map((relationship) => [relationship, { loading: true }])));
    relationships.forEach((relationship) => {
      fetchEntityRelationship(tenantName, entityType, objectId, relationship, { skip: 0, limit: 1, signal: controller.signal })
        .then((response) => setCounts((current) => ({ ...current, [relationship]: { loading: false, count: response.count } })))
        .catch((error) => {
          if (!isAbort(error)) setCounts((current) => ({ ...current, [relationship]: { loading: false, error } }));
        });
    });
    return () => controller.abort();
  }, [tenantName, entityType, objectId, relationships]);

  const runGraphRequest = useCallback(
    async (request: Parameters<typeof loadGraph>[0]) => {
      try {
        return await loadGraph(request);
      } catch (error) {
        if (!isAbort(error)) setNavigationError(requestErrorMessage(error));
        return false;
      }
    },
    [loadGraph],
  );

  const handleSectionToggle = (path: string[], section: InspectorSection, expanded: boolean) => {
    setNavigationError(undefined);
    if (!expanded) {
      setExpandedPath(path.slice(0, -1));
      return;
    }
    setExpandedPath(path);
    const relationship = section.relationship;
    const total = relationship ? counts[relationship]?.count ?? 0 : 0;
    if (relationship && entityType && total > 0 && total < GRAPH_RENDER_LIMIT) {
      void runGraphRequest((signal) => fetchEntityRelationshipGraph(tenantName, entityType, objectId, relationship, signal));
    }
  };

  const handleOpenRelated = async (item: BloodHoundRelatedEntity) => {
    const relatedObjectId = item.objectID || item.props?.objectid;
    if (!relatedObjectId) return;
    setNavigationError(undefined);
    const loaded = await runGraphRequest((signal) => fetchNodeGraph(tenantName, relatedObjectId, signal));
    if (!loaded) return;
    const match = useADStore.getState().graphNodes.find((graphNode) => graphNode.objectId === relatedObjectId);
    if (match) selectNode(match);
    else setNavigationError("The selected object could not be found in BloodHound.");
  };

  const kindsSet = useMemo(() => new Set(kinds ?? []), [kinds]);
  const zone = tags.find((tag) => tag.type === ZONE_TAG_TYPE && kindsSet.has(tagNameToKind(tag.name)))?.name;
  const labelNames = tags
    .filter((tag) => tag.type === LABEL_TAG_TYPE && kindsSet.has(tagNameToKind(tag.name)))
    .map((tag) => tag.name);
  const isTierZero = properties.isTierZero ?? (node.isTierZero || undefined);

  const basicFields: InspectorField[] = [
    { key: "zone", label: "Zone", value: zone },
    { key: "labels", label: "Labels", value: labelNames.length ? labelNames.join(", ") : undefined },
    { key: "nodeType", label: "Node Type", value: primaryKind },
    { key: "isTierZero", label: "Tier Zero", value: isTierZero },
    { key: "isOwnedObject", label: "Is Owned", value: properties.isOwnedObject },
    { key: "displayname", label: "Display Name", value: properties.displayname },
    { key: "objectid", label: "Object ID", value: properties.objectid ?? objectId },
  ]
    .filter((field) => field.value !== undefined && field.value !== "")
    .map((field) => ({ ...field, value: formatPrimitive(field.value, field.key) }));

  const objectFields = useMemo(() => buildObjectFields(properties), [properties]);
  const name = (properties.name as string) || node.label || objectId || "Unknown";

  return (
    <aside className="adi-panel" aria-label="Entity inspector">
      <header className="adi-header">
        <KindTile kind={primaryKind} />
        <div className="adi-header-copy">
          <h2 title={name}>{name}</h2>
          <div className="adi-header-meta">
            <span className="adi-chip">{primaryKind}</span>
            {isTierZero === true && (
              <span className="adi-chip" data-tone="danger">
                <Gem />
                Tier Zero
              </span>
            )}
            {node.isOwnedObject && (
              <span className="adi-chip" data-tone="warning">
                Owned
              </span>
            )}
          </div>
        </div>
        <button type="button" className="adi-close" aria-label="Close inspector" onClick={() => selectNode(null)}>
          <X />
        </button>
      </header>

      <div className="adi-scroll">
        {navigationError && <p className="adi-inline-error adi-banner">{navigationError}</p>}

        <section className="adi-card">
          <CollapsibleHeader
            label="Object Information"
            expanded={objectInfoOpen}
            loading={infoLoading}
            depth={0}
            onToggle={() => setObjectInfoOpen((open) => !open)}
          />
          {objectInfoOpen && (
            <div className="adi-fields">
              {[...basicFields, ...objectFields].map((field) => (
                <FieldRow key={field.key} field={field} />
              ))}
            </div>
          )}
        </section>

        {sections.length > 0 && (
          <section className="adi-card">
            <SectionTree
              sections={sections}
              depth={0}
              parentPath={[]}
              expandedPath={expandedPath}
              counts={counts}
              onToggle={handleSectionToggle}
              renderList={(section, total) =>
                entityType && section.relationship ? (
                  <RelationshipList
                    key={`${objectId}-${section.relationship}`}
                    tenantName={tenantName}
                    entityType={entityType}
                    objectId={objectId}
                    relationship={section.relationship}
                    total={total}
                    onOpen={handleOpenRelated}
                  />
                ) : (
                  <></>
                )
              }
            />
          </section>
        )}
      </div>
    </aside>
  );
}

export function ADInspector() {
  const { selectedNode, selectedTenant } = useADStore();
  if (!selectedNode || !selectedTenant) return null;
  return <InspectorPanel key={`${selectedTenant}-${selectedNode.objectId || selectedNode.id}`} node={selectedNode} tenantName={selectedTenant} />;
}
